import Editor, { useMonaco } from "@monaco-editor/react";
import { useEffect, useRef, useState } from "react";

import { useTheme } from "../../../../context/ThemeContext";
import PythonHeader from "./PythonHeader";
import PythonOutput from "./PythonOutput";

const TIMEOUT_MS = 1200;

const editorOptions = {
  minimap: { enabled: false },
  fontSize: 14,
  wordWrap: "off",
  lineNumbers: "on",
  scrollBeyondLastLine: false,
  smoothScrolling: true,
  renderLineHighlight: "all",
  tabSize: 2,
  formatOnType: true,
  automaticLayout: true,
  cursorStyle: "line",
  folding: true,
  suggestOnTriggerCharacters: true,
  quickSuggestions: { other: true, comments: true, strings: true },
  bracketPairColorization: { enabled: true },
  codeLens: true,
  links: true,
  matchBrackets: "always",
};

const PythonCodeEditor = ({
  isTeacher = false,
  initialCode,
  python,
  setPython,
  resetPythonCode = () => {},
  hasCodeContent = () => {},
  reRunCode = false,
}) => {
  const { theme } = useTheme();
  const [output, setOutput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isHorizontal, setIsHorizontal] = useState(true);
  const [pendingInputPrompt, setPendingInputPrompt] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const terminalRef = useRef(null);
  const workerRef = useRef(null);
  const timeoutRef = useRef(null);

  const monaco = useMonaco();

  useEffect(() => {
    if (!monaco) return;

    monaco.editor.defineTheme("custom-light", {
      base: "vs",
      inherit: true,
      rules: [],
      colors: {
        "editor.background": "#ffffff",
        "editor.lineHighlightBackground": "#f0f0f0",
      },
    });

    monaco.editor.defineTheme("custom-dark", {
      base: "vs-dark",
      inherit: true,
      rules: [],
      colors: {
        "editor.background": "#1e272e",
        "editor.lineHighlightBackground": "#2a2d2e",
      },
    });

    monaco.editor.setTheme(theme === "dark" ? "custom-dark" : "custom-light");
  }, [monaco, theme]);

  useEffect(() => {
    setPython(initialCode?.python || "");
    if (initialCode?.python) {
      onRunCode(initialCode.python);
    }
  }, [initialCode?.python, reRunCode]);

  const handleWorkerMessages = (event) => {
    const { type, text, prompt } = event.data;

    if (type === "output") {
      setOutput((prev) => prev + text);
    } else if (type === "error") {
      clearTimeoutIfExists();
      setOutput((prev) => prev + "\n** Error **\n" + text);
      setIsRunning(false);
      setPendingInputPrompt(null);
      setIsLoading(false);
    } else if (type === "input-request") {
      clearTimeoutIfExists(); // Pause auto-stop during input
      setPendingInputPrompt(prompt || "");
    } else if (type === "done") {
      clearTimeoutIfExists();
      setIsRunning(false);
      setPendingInputPrompt(null);
      setIsLoading(false);
    } else if (type === "stopped") {
      clearTimeoutIfExists();
      setOutput((prev) => prev + "\n** Execution interrupted **");
      setIsRunning(false);
      setPendingInputPrompt(null);
      setIsLoading(false);
    }
  };

  // Initialize worker once
  useEffect(() => {
    workerRef.current = new Worker("/skulptWorker.js");
    workerRef.current.onmessage = handleWorkerMessages;

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  const resetTimeout = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
        setOutput((prev) => prev + "\n⛔ Auto-stopped: code entered infinite loop");
        setIsRunning(false);
        setPendingInputPrompt(null);
        setIsLoading(false);
      }
    }, TIMEOUT_MS);
  };

  const clearTimeoutIfExists = () => {
    clearTimeout(timeoutRef.current);
  };

  const onRunCode = (codeParam) => {
    workerRef.current = new Worker("/skulptWorker.js");
    workerRef.current.onmessage = handleWorkerMessages;
    const codeToRun = codeParam ?? python ?? "";
    if (!workerRef.current) {
      setOutput((prev) => prev + "\n** Worker not available **");
      return;
    }

    // If already running, stop first (terminate and recreate)
    if (isRunning && workerRef.current) {
      try {
        workerRef.current.terminate();
      } catch {}
      workerRef.current = null;
    }

    // clear previous output and start
    setOutput("");
    setIsLoading(true);
    setIsRunning(true);
    setPendingInputPrompt(null);

    if (workerRef.current) {
      workerRef.current.postMessage({ type: "run", code: codeToRun });
    } else {
      workerRef.current = new Worker("/skulptWorker.js");
      workerRef.current.onmessage = handleWorkerMessages;
      workerRef.current.postMessage({ type: "run", code: codeToRun });
    }
    resetTimeout(); // Start timeout after code is posted to worker
  };

  const handleStop = () => {
    if (workerRef.current) {
      try {
        workerRef.current.postMessage({ type: "stop" });
      } catch {}
      try {
        workerRef.current.terminate();
      } catch {}
      workerRef.current = null;
    }

    setIsRunning(false);
    setIsLoading(false);
    setPendingInputPrompt(null);
    setOutput((prev) => prev + "\n** Execution interrupted **");
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "stop" });
    setIsRunning(false);
    setPendingInputPrompt(null);
  };

  const resetPythonCodeAndRunCode = () => {
    resetPythonCode();
    onRunCode("");
    setOutput("");
  };
  // Submit input value to worker
  const submitInput = () => {
    if (!workerRef.current) return;
    workerRef.current.postMessage({ type: "input-response", value: inputValue });
    setInputValue("");
    setPendingInputPrompt(null);
    resetTimeout(); // Resume auto-kill timer
  };

  return (
    <div className="ltr-ignore">
      <PythonHeader
        onRunCode={onRunCode}
        onStopCode={handleStop}
        isLoading={isLoading}
        isRunning={isRunning}
        setIsHorizontal={setIsHorizontal}
        isHorizontal={isHorizontal}
        resetPythonCodeAndRunCode={resetPythonCodeAndRunCode}
        hasCodeContent={hasCodeContent}
        isTeacher={isTeacher}
      />

      <div className={`flex ${isHorizontal ? "gap-2" : "flex-col space-y-2"}`}>
        <div
          className={`w-full rounded-lg border border-gray-50 bg-white p-0 pb-4 shadow-md dark:border-dark-border dark:bg-[#1e272e] ${
            isHorizontal ? "lg:w-1/2" : ""
          }`}
        >
          <Editor
            options={editorOptions}
            height={isHorizontal ? "60vh" : "30vh"}
            theme={theme === "dark" ? "custom-dark" : "custom-light"}
            language="python"
            value={python}
            onChange={(value) => setPython(value)}
          />
        </div>

        <PythonOutput
          output={output}
          isRunning={isRunning}
          isHorizontal={isHorizontal}
          terminalRef={terminalRef}
          pendingInputPrompt={pendingInputPrompt}
          submitInput={submitInput}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />
      </div>
    </div>
  );
};

export default PythonCodeEditor;
